/**
 * Client-side persistence for per-user agent UI preferences:
 *   - the most-recently-entered agent (drives the signed-in home redirect)
 *   - which agents' first-run intro tour the user has already seen
 *
 * Backed by localStorage. Storage access is best-effort: a disabled or full
 * store degrades to "no preference recorded" rather than breaking navigation,
 * since none of this is correctness-critical — it only tunes where the user
 * lands. Reads of a missing/invalid value return the same neutral default.
 */

import { isAgentSlug, type AgentSlug } from './registry.ts';

const LAST_AGENT_KEY = 'om.lastAgent';
const INTRO_SEEN_PREFIX = 'om.introSeen.';
// Global (not per-agent): the install step is offered once per device, the
// first time the user goes through any agent's first-run tour.
const INSTALL_PROMPT_SEEN_KEY = 'om.installPromptSeen';

/** Read a key, treating an unavailable store or read error as "absent". */
function readItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Write a key, silently degrading when the store is unavailable or full. */
function writeItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Best-effort: a failed preference write must not interrupt the user.
  }
}

/**
 * The last agent the user entered, or null when none is recorded (or the
 * stored value no longer names a known agent). Storage-validity only —
 * whether the CURRENT user may still enter it is an eligibility question
 * the caller answers with `isAgentEnabledFor` (agentAccess.ts), since
 * eligibility is per-user and this module has no user in scope.
 */
export function getLastAgent(): AgentSlug | null {
  const value = readItem(LAST_AGENT_KEY);
  if (value === null || !isAgentSlug(value)) {
    return null;
  }
  return value;
}

export function setLastAgent(slug: AgentSlug): void {
  writeItem(LAST_AGENT_KEY, slug);
}

export function hasSeenIntro(slug: AgentSlug): boolean {
  return readItem(INTRO_SEEN_PREFIX + slug) === '1';
}

export function markIntroSeen(slug: AgentSlug): void {
  writeItem(INTRO_SEEN_PREFIX + slug, '1');
}

/** Whether the user has already been shown the "add to home screen" step. */
export function hasSeenInstallPrompt(): boolean {
  return readItem(INSTALL_PROMPT_SEEN_KEY) === '1';
}

export function markInstallPromptSeen(): void {
  writeItem(INSTALL_PROMPT_SEEN_KEY, '1');
}
